import { useEffect, useState } from "react";
import type { JSX } from "react";
import { useAiModels } from "./hooks/useAiModels";
import type { AiProvider } from "./types";
import { PageSectionHeader } from "../../components/ui";
import {
  AvailableRow,
  ConnectedRow,
  GroupCard,
  GroupLabel,
  PROVIDER_BLURB,
  ProviderKeyModal,
} from "./aiModelsShared";

// A sensible default Codex model. IDs are account-specific (the ChatGPT/Codex
// backend accepts a fixed set per account) and not in the models.dev catalog,
// so we provision one on connect; it can be removed on the Models page.
const CODEX_DEFAULT_MODEL_ID = "gpt-5.5";
const CODEX_DEFAULT_MODEL_NAME = "GPT-5.5 (Codex)";

// Providers settings page: connect the providers whose models the Magnis Agent
// can use — Connected (with a key/subscription) and Available (with Connect).
export function ProvidersPanel(): JSX.Element {
  const {
    providers,
    models,
    loading,
    loadError,
    connectProvider,
    updateProvider,
    deleteProvider,
    createModel,
    setDefault,
    subscriptionStatus,
    subscriptionStatusError,
    loadSubscriptionStatus,
  } = useAiModels();
  // Provider whose modal is open (Connect / Replace key / Enable subscription).
  const [modalProvider, setModalProvider] = useState<AiProvider | null>(null);

  const hasSubscription = providers.some((p) => p.auth_kind === "codex_subscription");
  useEffect(() => {
    if (hasSubscription) void loadSubscriptionStatus();
  }, [hasSubscription, loadSubscriptionStatus]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-content-tertiary text-sm">
        Loading...
      </div>
    );
  }

  // A subscription counts as connected only when it is ENABLED (the chat path
  // threads only enabled providers). Otherwise it lives in "Available".
  const isUsable = (p: AiProvider): boolean =>
    p.api_key_set || (p.auth_kind === "codex_subscription" && p.enabled);
  const connectedProviders = providers.filter(isUsable);
  const availableProviders = providers.filter((p) => !isUsable(p));

  // Enable a subscription: turn the provider on, provision a default Codex
  // model if none exists, and point the Magnis Agent's chat default at it.
  const enableSubscription = async (provider: AiProvider): Promise<void> => {
    await updateProvider(provider.id, { enabled: true });
    const existing = models.find(
      (m) =>
        m.provider_id === provider.id &&
        m.model_id === CODEX_DEFAULT_MODEL_ID &&
        m.capability === "chat",
    );
    const model =
      existing ??
      (await createModel({
        provider_id: provider.id,
        model_id: CODEX_DEFAULT_MODEL_ID,
        name: CODEX_DEFAULT_MODEL_NAME,
      }));
    await setDefault("chat", model.id);
  };

  const disconnect = async (provider: AiProvider): Promise<void> => {
    if (provider.auth_kind === "codex_subscription") {
      await updateProvider(provider.id, { enabled: false });
    } else {
      await deleteProvider(provider.id);
    }
  };

  return (
    <div className="px-6 py-5 space-y-4">
      <PageSectionHeader
        title="Providers"
        description="Connect the providers whose models the Magnis Agent can use."
      />

      {loadError !== null && <span className="text-xs text-red-400 block">{loadError}</span>}

      {connectedProviders.length > 0 && (
        <div className="space-y-1.5">
          <GroupLabel>Connected</GroupLabel>
          <GroupCard>
            {connectedProviders.map((provider) => (
              <ConnectedRow
                key={provider.id}
                provider={provider}
                status={subscriptionStatus}
                statusError={subscriptionStatusError}
                onReplaceKey={() => { setModalProvider(provider); }}
                onDisconnect={() => disconnect(provider)}
              />
            ))}
          </GroupCard>
        </div>
      )}

      {availableProviders.length > 0 && (
        <div className="space-y-1.5">
          <GroupLabel>Available</GroupLabel>
          <GroupCard>
            {availableProviders.map((provider) => (
              <AvailableRow
                key={provider.id}
                provider={provider}
                blurb={PROVIDER_BLURB[provider.id]}
                onConnect={() => { setModalProvider(provider); }}
              />
            ))}
          </GroupCard>
        </div>
      )}

      {modalProvider && (
        <ProviderKeyModal
          provider={modalProvider}
          onSave={async (key) => {
            await connectProvider({
              id: modalProvider.id,
              name: modalProvider.name,
              base_url: modalProvider.base_url ?? "",
              api_key: key,
            });
          }}
          onEnable={
            modalProvider.auth_kind === "codex_subscription"
              ? (): Promise<void> => enableSubscription(modalProvider)
              : undefined
          }
          onClose={() => { setModalProvider(null); }}
        />
      )}
    </div>
  );
}
